import React, { useRef, useEffect, useState } from 'react';
import {
  Text,
  Badge,
  Box,
  Heading,
  HStack,
  Wrap,
  WrapItem,
  Button,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import DownloadButton from './DownloadButton';
import { fomratNumberSpace } from 'helpers';
import { useTranslation } from 'react-i18next';

export const MultiOptionField = ({
  header,
  elements,
  gridColumn,
  mode = 'default',
}: {
  header: string;
  elements: string[];
  gridColumn: any;
  mode?: 'default' | 'overflow';
}): JSX.Element => {
  const { t } = useTranslation();

  if (!elements || elements.length === 0) {
    return (
      <Box gridColumn={gridColumn}>
        <Heading fontSize='md' mb={2} marginInlineEnd={3}>
          {header}
        </Heading>
        <HStack spacing={2}>
          <Wrap>
            <Text fontSize='sm'>{t('none', 'Brak')}</Text>
          </Wrap>
        </HStack>
      </Box>
    );
  }

  const renderFields = () => {
    if (mode === 'overflow') {
      const hasMoreThanThree = elements.length > 3;

      if (hasMoreThanThree) {
        const allElements = elements.join(', ');
        const firstThree = elements.slice(0, 3);
        return (
          <>
            {firstThree.map((element, index) => (
              <WrapItem key={index}>
                <BadgeWithOverflow element={element} />
              </WrapItem>
            ))}
            <WrapItem flexBasis='100%'>
              <Tooltip label={allElements} hasArrow placement='bottom'>
                <Badge
                  fontSize='sm'
                  px={1}
                  bgColor='WPPNavy.400'
                  color='white'
                  cursor='pointer'
                >
                  {t('showAll', 'POKAŻ WSZYSTKIE')}
                </Badge>
              </Tooltip>
            </WrapItem>
          </>
        );
      }

      return elements.map((element, index) => (
        <WrapItem key={index}>
          <BadgeWithOverflow element={element} />
        </WrapItem>
      ));
    }

    // default mode
    return elements.map((element, index) => (
      <WrapItem key={index}>
        <BadgeT element={element} />
      </WrapItem>
    ));
  };

  return (
    <Box gridColumn={gridColumn}>
      <Heading fontSize='md' mb={2} marginInlineEnd={3}>
        {header}
      </Heading>
      <HStack spacing={2}>
        <Wrap>{renderFields()}</Wrap>
      </HStack>
    </Box>
  );
};

export const BadgeT = ({ element }: { element: string }): JSX.Element => {
  return (
    <Badge key={element} fontSize='sm' px={1}>
      {element}
    </Badge>
  );
};

export const BadgeWithOverflow = ({
  element,
  width = '15ch',
}: {
  element: string;
  width?: string;
}): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth);
    }
  }, [element]);

  return (
    <Tooltip label={element} isDisabled={!isOverflowing} placement='top'>
      <Badge
        ref={ref}
        fontSize='sm'
        px={1}
        maxWidth={width}
        overflow='hidden'
        textOverflow='ellipsis'
        whiteSpace='nowrap'
        cursor={isOverflowing ? 'pointer' : 'default'}
      >
        {element}
      </Badge>
    </Tooltip>
  );
};

export const SingleField = ({
  header,
  value,
  gridColumn,
  isDate,
  layout = 'vertical',
}: {
  header: string;
  value: string | number | null | undefined;
  gridColumn?: any;
  isDate?: boolean;
  layout?: 'vertical' | 'horizontal';
}): JSX.Element => {
  const { t } = useTranslation();
  if (value === undefined || value === '' || value === null) {
    value = t<string>('none', 'Brak');
  }

  if (layout === 'horizontal') {
    return (
      <Flex gridColumn={gridColumn} align='center' gap={3}>
        <Heading fontSize='md' marginInlineEnd={0} mb={0}>
          {header}
        </Heading>
        <Text fontSize='md'>{isDate ? value : fomratNumberSpace(value)}</Text>
      </Flex>
    );
  }

  return (
    <Box gridColumn={gridColumn}>
      <Heading fontSize='md' mb={2} marginInlineEnd={3}>
        {header}
      </Heading>
      <Text fontSize='md'>{isDate ? value : fomratNumberSpace(value)}</Text>
    </Box>
  );
};

export const FileField = ({
  header,
  value,
  gridColumn,
}: {
  header: string;
  value: {
    files: { id: number; name?: string; uploadedFile?: string }[];
    providedUrls: string[];
  };
  gridColumn: any;
}): JSX.Element => {
  const { t } = useTranslation();
  const { files, providedUrls } = value;

  const hasFiles = files && files.length > 0;
  const hasUrls = providedUrls && providedUrls.length > 0;

  return (
    <Box ml={4} gridColumn={gridColumn}>
      <Heading fontSize='md' mb={1}>
        {header}
      </Heading>
      {hasUrls && (
        <Text fontSize='sm' mb={2}>
          {t('providedLinks', 'Dostarczone linki:')}
        </Text>
      )}
      {hasUrls &&
        providedUrls?.map((url, index) => (
          <Button
            as={Link}
            to={{ pathname: url }}
            key={index}
            fontSize='sm'
            target='_blank'
            mb={2}
          >
            {t('link', 'Link')} {index + 1}
          </Button>
        ))}
      {hasFiles && (
        <Text fontSize='sm' mb={2}>
          {t('files', 'Pliki:')}
        </Text>
      )}
      {hasFiles &&
        files?.map(file =>
          file.uploadedFile ? (
            <DownloadButton
              uploadedFile={file.uploadedFile}
              fileName={file.name ?? ''}
              title={t('download', 'Pobierz')}
              description={t('downloadFile', 'Pobierz plik')}
              key={file.id}
            />
          ) : null,
        )}
      {!hasFiles && !hasUrls && <Text fontSize='sm'>{t('none', 'Brak')}</Text>}
    </Box>
  );
};
